import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCities,
  matchServiceCity,
  reverseGeocode,
  searchPlaces,
  ServiceCity,
} from '../services/location';

/**
 * Whether the pin behind the address on screen is somewhere we can actually
 * send a professional - for Home, which has never asked this before.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS RE-RUNS THE GEOCODE RATHER THAN TRUSTING `/geo/serviceability`
 * ---------------------------------------------------------------------------
 * The backend also has a single-purpose `checkServiceability` endpoint that
 * resolves a pin against its own area grid - cheaper, one call, no geocoder.
 * `LocationScreen` deliberately does not use it: that grid is unmapped in
 * places, and it answers `null` for pins in the middle of Indore, a city we
 * DO cover. Trusting it here would tell some customers standing in our best
 * city that we do not serve them.
 *
 * So this follows the same path `LocationScreen` already trusts: reverse-
 * geocode the pin for `cityCandidates`, then match those against `/cities`
 * by name. Same two calls, same comparison, same three-state answer - see
 * `LocationScreen`'s own `covered` for the fuller reasoning.
 *
 * Three states, not two. `null` is "not known yet" - no pin, or the calls
 * have not landed - and Home must say nothing rather than guess, because a
 * false "not covered" loses a ready customer and a false silence is the
 * failure at checkout this hook exists to move earlier.
 */
/**
 * Coverage, and the city it was decided by.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CITY CAME OUT OF HERE AND NOT FROM SOMEWHERE NEW
 * ---------------------------------------------------------------------------
 * The home screen's published configuration is targeted per city, and the
 * screen had no city id to ask with - so it asked globally, and a tree
 * published for Indore was invisible to every customer standing in Indore.
 * Nothing looked broken from either end: the admin console said "Published",
 * and the app quietly drew its compiled screen.
 *
 * This hook has always known the answer. It reverse-geocodes the pin and
 * matches the result against `/cities`, and then threw away everything but
 * the yes/no. Returning the row instead costs no extra request.
 *
 * `useCoverage` below is unchanged for its callers - it is this, narrowed
 * back to the boolean.
 */
export function useServiceCity(
  coords: { lat: number; lng: number } | undefined,
  /**
   * The header line, used only when there is no pin.
   *
   * ---------------------------------------------------------------------------
   * WHY A LINE OF TEXT IS A LAST RESORT AND NOT THE INPUT
   * ---------------------------------------------------------------------------
   * Every address picked from the map carries coordinates, and those are what
   * this hook wants: they are exact, and they are what the booking travels on.
   *
   * But an address saved before the app stored pins alongside lines has none -
   * on a real device the whole of it was the string "Mumbai, Mumbai,
   * Maharashtra, India" and nothing else. With no coordinates this hook
   * answered `null` (unknown) forever, so the coming-soon banner never
   * appeared for exactly the customers it was written for.
   *
   * Rather than make them re-pick an address to get an answer, the line is
   * run through `/geo/search` - the forward geocode - to recover a pin. It is
   * strictly a fallback: `coords` wins whenever it exists, because a line of
   * text can be ambiguous in a way a pin never is.
   */
  addressLine?: string,
): { covered: boolean | null; city: ServiceCity | null } {
  const [cities, setCities] = useState<ServiceCity[] | null>(null);
  const [candidates, setCandidates] = useState<{
    key: string;
    cityCandidates: string[] | undefined;
  } | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /* Fetched once, like `LocationScreen`'s - the list changes when the
     business enters a new city, not while someone is looking at Home. */
  useEffect(() => {
    const controller = new AbortController();
    fetchCities(controller.signal)
      .then(found => {
        if (alive.current) {
          setCities(found);
        }
      })
      .catch(() => {
        /* Stays null - read as "unknown", not "uncovered". */
      });
    return () => controller.abort();
  }, []);

  /*
   * What is being asked about, as one string.
   *
   * The pin when there is one, the line when there is not. It identifies the
   * question so a late answer about a PREVIOUS address can be discarded - see
   * the `candidates.key` guard below - and it is what the effect re-runs on.
   */
  const key = coords
    ? `${coords.lat.toFixed(4)},${coords.lng.toFixed(4)}`
    : addressLine && addressLine.trim()
    ? `line:${addressLine.trim()}`
    : null;

  useEffect(() => {
    if (!key) {
      return;
    }

    const controller = new AbortController();

    /*
     * Two ways to the same three words.
     *
     * With a pin it is the reverse geocode, exactly as before. Without one the
     * line goes through `/geo/search` and the FIRST result's coordinates are
     * reverse-geocoded - the round trip is needed because search returns a
     * pin and a title but not the widening `cityCandidates` chain that
     * `isServiceable` matches on.
     *
     * Both paths end at the same place: a list of city names for the one
     * comparison this hook makes.
     */
    const resolve = coords
      ? reverseGeocode(coords.lat, coords.lng, controller.signal)
      : searchPlaces(addressLine!.trim(), controller.signal).then(results => {
          const first = results[0];
          if (!first) {
            /* A line nothing can be found for stays unknown rather than
               becoming "not covered" - the address may be perfectly real and
               merely unsearchable. */
            throw new Error('No place matched the saved address line');
          }
          return reverseGeocode(first.lat, first.lng, controller.signal);
        });

    resolve
      .then(resolved => {
        if (alive.current) {
          setCandidates({ key, cityCandidates: resolved.cityCandidates });
        }
      })
      .catch(() => {
        /* The question changed hands with no answer - `candidates` stays
           whatever it was, which the `key` guard below already discards. */
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /*
   * Memoised so the object identity is stable across renders - it is read
   * straight into an effect's dependencies by `HomeScreen`, and a fresh object
   * every render would re-fetch the home configuration on every keystroke.
   */
  const answered =
    !!cities &&
    !!candidates &&
    candidates.key === key &&
    !!candidates.cityCandidates?.length;
  const city =
    answered && cities && candidates
      ? matchServiceCity(candidates.cityCandidates, cities)
      : null;

  return useMemo(
    () => ({ covered: answered ? city !== null : null, city }),
    [answered, city],
  );
}

/**
 * The same question, as the boolean every existing caller wants.
 *
 * Three states, not two - see the note on the hook above.
 */
export function useCoverage(
  coords: { lat: number; lng: number } | undefined,
  addressLine?: string,
): boolean | null {
  return useServiceCity(coords, addressLine).covered;
}
